import React from "react";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import DriverStatusIcon from "@/assets/icons/DriverStatusIcon.svg";
import DriverProfileCardWatermark from "@/assets/backgrounds/DriverProfileCardWatermark.svg";
import { cn, pickInitials } from "@/lib/utils";
import i18n from "@/lang/i18n";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Button } from "../ui/button";
import {
  ActivityIcon,
  CircleChevronUpIcon,
  Clock4Icon,
  LogOutIcon,
} from "lucide-react";
import { useTheme } from "@/themeConfigs/ThemeContext";

interface DriverProfileCardProps {
  name: string;
  driverId: string;
  hub: string;
  entryType: "report_at" | "login_token";
  entryTypeText: string;
  statusType: "safe" | "danger" | "caution";
  driverStatus: string;
  handleLogout: () => void;
  showNecessaryInfoOnly?: boolean;
}

const DriverProfileCard: React.FC<DriverProfileCardProps> = ({
  handleLogout,
  name,
  driverId,
  hub,
  entryType,
  entryTypeText,
  statusType,
  driverStatus,
  showNecessaryInfoOnly = false,
}) => {
  const { theme } = useTheme();
  let bgColor = "bg-[#ffeeee]";
  let borderColor = "border-[#fa0404]";
  let textColor = "text-[#fa0404]";

  if (statusType === "caution") {
    bgColor = "bg-[#fffbef]";
    borderColor = "border-[#d9aa06]";
    textColor = "text-[#d9aa06]";
  } else if (statusType === "safe") {
    bgColor = "bg-[#e9ffe9]";
    borderColor = "border-[#4cb726]";
    textColor = "text-[#4cb726]";
  }

  return (
    <div
      className={cn(
        "relative w-full h-80 rounded-lg mb-8",
        showNecessaryInfoOnly && "h-[140px]"
      )}
    >
      <img
        className={cn(
          "w-full object-cover rounded-3xl h-80",
          showNecessaryInfoOnly && "h-[140px]"
        )}
        alt="bg-driver-profile-card-watermark"
        src={DriverProfileCardWatermark}
      />
      <div className="absolute inset-0 w-100 py-3 px-3">
        {!showNecessaryInfoOnly && (
          <h5 className="font-bold  text-xl text-secondary flex flex-row justify-center">
            {i18n.t("welcome")}!
          </h5>
        )}
        <div>
          <div className="flex flex-row gap-3 py-3">
            <Popover>
              <PopoverTrigger disabled={!theme.layout.backButton}>
                <Avatar className="h-14 w-14 text-2xl">
                  <AvatarImage />
                  <AvatarFallback>{pickInitials(name)}</AvatarFallback>
                </Avatar>
              </PopoverTrigger>
              <PopoverContent className="w-36 p-2 flex flex-col items-center">
                <Button
                  onClick={handleLogout}
                  variant="ghost"
                  className="w-full flex items-center justify-center gap-2"
                >
                  <LogOutIcon className="w-4 h-4" />
                  Logout
                </Button>
              </PopoverContent>
            </Popover>
            <div>
              <p className="font-semibold text-white text-base truncate ">
                {name}
              </p>
              <p className="font-bold text-white text-2xl truncate ">
                {driverId}
              </p>
              <p className="font-medium text-secondary truncate ">{hub}</p>
            </div>
          </div>
          {!showNecessaryInfoOnly && (
            <div className="rounded-2xl bg-secondary border border-secondary w-full">
              <div className=" w-full flex flex-col items-center gap-1 justify-center py-2">
                <div className="flex flex-row items-center justify-center gap-2">
                  {entryType === "report_at" ? (
                    <>
                      <Clock4Icon className="text-white" />
                    </>
                  ) : (
                    <>
                      <CircleChevronUpIcon className="text-white" />
                    </>
                  )}
                  <p className="leading-4 text-sm text-white ">
                    {i18n.t(entryType)}:
                  </p>
                </div>
                <div className="flex items-center">
                  <p className="font-bold leading-8 text-2xl text-primary">
                    {entryTypeText}
                  </p>
                </div>
              </div>
              <div className="rounded-b-2xl w-full py-3 bg-white box-content flex flex-col justify-center items-center">
                <div className="flex flex-row gap-2 items-center justify-center ">
                  <ActivityIcon />
                  <p className="text-sm text-secondary">
                    {i18n.t("current_status")}
                  </p>
                </div>
                <div
                  className={`${borderColor} ${bgColor} w-11/12 rounded-xl border flex justify-center items-center mt-2 px-2 py-2`}
                >
                  <p className={`${textColor}  leading-4 text-md text-center`}>
                    {driverStatus}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DriverProfileCard;
